import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Search, Loader2, User, Video } from 'lucide-react';
import { useAppUsers } from '@/hooks/useAppUsers';

interface DirectCallModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (userId: string) => void;
  isStarting?: boolean;
}

export function DirectCallModal({ isOpen, onClose, onSelect, isStarting }: DirectCallModalProps) {
  const { users, loading } = useAppUsers();
  const [searchQuery, setSearchQuery] = useState('');

  const availableUsers = users
    .filter(
      (user) =>
        user.active &&
        (searchQuery === '' ||
          user.display_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          user.email?.toLowerCase().includes(searchQuery.toLowerCase()))
    )
    .sort((a, b) => {
      const nameA = (a.display_name || a.email || '').toLowerCase();
      const nameB = (b.display_name || b.email || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });

  const resetAndClose = () => {
    setSearchQuery('');
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={resetAndClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Video className="w-5 h-5 text-primary" />
            Chamar Pessoa
          </DialogTitle>
          <DialogDescription>
            Selecione o usuário para iniciar a videochamada
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Buscar usuário..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>

          <ScrollArea className="h-[300px] border rounded-lg">
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            ) : availableUsers.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center p-4">
                <User className="w-8 h-8 text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground">
                  {searchQuery ? 'Nenhum usuário encontrado' : 'Nenhum usuário disponível'}
                </p>
              </div>
            ) : (
              <div className="p-2 space-y-1">
                {availableUsers.map((user) => (
                  <button
                    key={user.id}
                    disabled={isStarting}
                    onClick={() => onSelect(user.id)}
                    className="w-full flex items-center gap-3 p-3 rounded-lg transition-colors hover:bg-muted border-2 border-transparent"
                  >
                    <Avatar className="w-10 h-10">
                      <AvatarFallback className="bg-primary/20 text-primary font-semibold">
                        {user.display_name
                          ?.split(' ')
                          .map((n) => n[0])
                          .join('')
                          .toUpperCase()
                          .slice(0, 2) || '??'}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 text-left">
                      <p className="font-medium text-sm">{user.display_name || 'Sem nome'}</p>
                      <p className="text-xs text-muted-foreground">{user.email}</p>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </ScrollArea>
        </div>
      </DialogContent>
    </Dialog>
  );
}
